import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  CheckCircle,
  Undo,
  Comment as CommentIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

interface TaskRow {
  _id?: string;
  id?: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  assignedTo: string;
  dueDate: string;
  progress: number;
  createdAt: string;
  isEms?: boolean;
  taskType?: string;
  remark?: string;
  projectName?: string;
  /** Normalized for Done / Unmark actions */
  etmsAssigneeStatus?: 'open' | 'done';
  /** Raw value from API (e.g. pending, open, done) for display */
  etmsAssigneeStatusRaw?: string;
  etmsAssigneeComment?: string;
  assignedAt?: string;
}

const Tasks: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [etmsFilter, setEtmsFilter] = useState<'all' | 'open' | 'done'>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTask, setSelectedTask] = useState<TaskRow | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [sortBy, setSortBy] = useState<'dueDate' | 'priority' | 'status'>('dueDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentTaskId, setCommentTaskId] = useState<string | null>(null);

  const mapTask = (task: any): TaskRow => {
    const idStr = task._id != null ? String(task._id) : task.id != null ? String(task.id) : undefined;
    const isEms = !!(task.assigneeEmail || task.projectName || task.taskType);
    const title = task.title || task.projectName || 'Untitled';
    const description = task.description || task.remark || '';
    const rawEtms = task.etmsAssigneeStatus != null ? String(task.etmsAssigneeStatus).toLowerCase() : '';
    const etmsDone = rawEtms === 'done';
    return {
      _id: idStr,
      id: idStr,
      title,
      description,
      status: task.status || (etmsDone ? 'completed' : 'assigned'),
      priority: task.priority || '—',
      assignedTo:
        typeof task.assignedTo === 'string'
          ? task.assignedTo
          : task.assignedTo?.name || task.assigneeName || '—',
      dueDate: task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '—',
      progress: task.progress ?? 0,
      createdAt: task.createdAt,
      isEms,
      taskType: task.taskType ? String(task.taskType) : undefined,
      remark: task.remark != null ? String(task.remark) : undefined,
      projectName: task.projectName ? String(task.projectName) : undefined,
      etmsAssigneeStatus: etmsDone ? 'done' : 'open',
      etmsAssigneeStatusRaw: task.etmsAssigneeStatus != null ? String(task.etmsAssigneeStatus) : undefined,
      etmsAssigneeComment: task.etmsAssigneeComment != null ? String(task.etmsAssigneeComment) : '',
      assignedAt: task.assignedAt ? String(task.assignedAt) : undefined,
    };
  };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters: Record<string, string> = {};
      if (isAdmin && statusFilter !== 'all') filters.status = statusFilter;
      if (isAdmin && priorityFilter !== 'all') filters.priority = priorityFilter;
      if (isAdmin && assignedToFilter) filters.assignedTo = assignedToFilter;
      if (isAdmin && dateFilter) filters.date = dateFilter;
      filters.sortBy = sortBy;
      filters.sortOrder = sortOrder;

      const response = await apiService.getTasks(filters);
      const fetchedTasks = response.tasks || response || [];

      const rows: TaskRow[] = fetchedTasks.map(mapTask);

      const normalizedSearch = searchTerm.trim().toLowerCase();
      let filtered = normalizedSearch
        ? rows.filter((row) => {
            const hay = [
              row.title,
              row.projectName,
              row.remark,
              row.description,
              row.taskType,
              row.assignedTo,
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return hay.includes(normalizedSearch);
          })
        : rows;

      if (!isAdmin && etmsFilter !== 'all') {
        filtered = filtered.filter((row) =>
          etmsFilter === 'done' ? row.etmsAssigneeStatus === 'done' : row.etmsAssigneeStatus === 'open'
        );
      }

      setTasks(filtered);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      setError('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, [
    searchTerm,
    statusFilter,
    etmsFilter,
    priorityFilter,
    assignedToFilter,
    dateFilter,
    sortBy,
    sortOrder,
    isAdmin,
  ]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, task: TaskRow) => {
    setAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTask(null);
  };

  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    const taskId = selectedTask._id || selectedTask.id;
    if (!taskId) return;
    
    try {
      await apiService.deleteTask(taskId);
      setSuccess('Task deleted successfully');
      fetchTasks();
    } catch (err: any) {
      console.error('Failed to delete task', err);
      setError(err?.response?.data?.message || 'Failed to delete task');
    }

    handleMenuClose();
  };

  const handleToggleDone = async (task: TaskRow) => {
    const tid = task._id || task.id;
    if (!tid) return;
    const nextDone = task.etmsAssigneeStatus !== 'done';
    setActionLoading(tid);
    setError(null);
    try {
      await apiService.updateMyTaskCompletion(tid, { done: nextDone });
      setSuccess(nextDone ? 'Marked as done' : 'Marked as open');
      fetchTasks();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Update failed');
    } finally {
      setActionLoading(null);
    }
  };

  const openCommentDialog = (task: TaskRow) => {
    const tid = task._id || task.id;
    if (!tid) return;
    setCommentTaskId(tid);
    setCommentDraft(task.etmsAssigneeComment || '');
    setCommentDialogOpen(true);
  };

  const submitComment = async () => {
    if (!commentTaskId) return;
    setActionLoading(commentTaskId);
    setError(null);
    try {
      await apiService.updateMyTaskCompletion(commentTaskId, {
        comment: commentDraft,
      });
      setSuccess('Comment saved');
      setCommentDialogOpen(false);
      fetchTasks();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save comment');
    } finally {
      setActionLoading(null);
    }
  };

  const submitCommentAndDone = async () => {
    if (!commentTaskId) return;
    setActionLoading(commentTaskId);
    setError(null);
    try {
      await apiService.updateMyTaskCompletion(commentTaskId, {
        done: true,
        comment: commentDraft,
      });
      setSuccess('Saved and marked done');
      setCommentDialogOpen(false);
      fetchTasks();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'assigned':
        return 'info';
      case 'on_hold':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {isAdmin ? 'Tasks Management' : 'My tasks'}
        </Typography>
        {isAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
            Create Task
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Search tasks..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 250 }}
          />
          {!isAdmin && (
            <TextField
              select
              label="Your progress"
              value={etmsFilter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEtmsFilter(e.target.value as 'all' | 'open' | 'done')}
              sx={{ minWidth: 160 }}
              SelectProps={{ native: true }}
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="done">Done</option>
            </TextField>
          )}
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150 }}
            SelectProps={{ native: true }}
          >
            <option value="all">All Status</option>
            <option value="assigned">Assigned</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On Hold</option>
          </TextField>
          {isAdmin && (
            <>
              <TextField
                select
                label="Priority"
                value={priorityFilter}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriorityFilter(e.target.value)}
                sx={{ minWidth: 150 }}
                SelectProps={{ native: true }}
              >
                <option value="all">All Priorities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </TextField>
              <TextField
                label="Employee (ID or name)"
                value={assignedToFilter}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAssignedToFilter(e.target.value)}
                sx={{ minWidth: 220 }}
                placeholder="Filter by employee"
              />
              <TextField
                label="Due Date"
                type="date"
                value={dateFilter}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <TextField
            select
            label="Sort By"
            value={sortBy}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSortBy(e.target.value as 'dueDate' | 'priority' | 'status')}
            sx={{ minWidth: 180 }}
            SelectProps={{ native: true }}
          >
            <option value="dueDate">Due Date</option>
            <option value="priority">Priority</option>
            <option value="status">Status</option>
          </TextField>
          <TextField
            select
            label="Order"
            value={sortOrder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSortOrder(e.target.value as 'asc' | 'desc')}
            sx={{ minWidth: 150 }}
            SelectProps={{ native: true }}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </TextField>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {isAdmin ? (
                  <>
                    <TableCell>Task</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>Project / task</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Instructions</TableCell>
                    <TableCell>Assigned</TableCell>
                    <TableCell>Your status</TableCell>
                    <TableCell>Comment</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 7} align="center">
                    <Typography variant="body2" sx={{ py: 4 }}>
                      No tasks found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : isAdmin ? (
                tasks.map((task: TaskRow, index: number) => (
                  <TableRow
                    key={task._id || task.id || `task-${index}`}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                    onClick={() => {
                      const taskId = task._id || task.id;
                      if (taskId) navigate(`/tasks/${taskId}`);
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{task.title}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {(task.description || '').substring(0, 50)}...
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{task.assignedTo}</TableCell>
                    <TableCell>
                      <Chip
                        label={(task.status || '').replace(/_/g, ' ')}
                        color={getStatusColor(task.status) as 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={task.priority}
                        color={getPriorityColor(task.priority) as 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{task.dueDate}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{task.progress}%</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          handleMenuClick(e, task);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                tasks.map((task: TaskRow, index: number) => {
                  const tid = task._id || task.id;
                  const busy = actionLoading === tid;
                  const raw = (task.etmsAssigneeStatusRaw || '').toLowerCase();
                  const statusLabel =
                    raw === 'done' || task.etmsAssigneeStatus === 'done'
                      ? 'Done'
                      : raw === 'pending'
                        ? 'Pending'
                        : raw && raw !== 'open'
                          ? task.etmsAssigneeStatusRaw || 'Open'
                          : 'Open';
                  return (
                    <TableRow key={task._id || task.id || `task-${index}`}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {task.projectName || task.title || '—'}
                        </Typography>
                        {task.isEms && (
                          <Chip label="EMS" size="small" sx={{ mt: 0.5, mr: 0.5 }} variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{task.taskType || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2">{task.remark || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        {task.assignedAt
                          ? new Date(task.assignedAt).toLocaleString()
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabel}
                          color={task.etmsAssigneeStatus === 'done' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap title={task.etmsAssigneeComment}>
                          {task.etmsAssigneeComment || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant={task.etmsAssigneeStatus === 'done' ? 'outlined' : 'contained'}
                          startIcon={task.etmsAssigneeStatus === 'done' ? <Undo /> : <CheckCircle />}
                          disabled={busy}
                          onClick={() => handleToggleDone(task)}
                          sx={{ mr: 1 }}
                        >
                          {task.etmsAssigneeStatus === 'done' ? 'Unmark' : 'Done'}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<CommentIcon />}
                          disabled={busy}
                          onClick={() => openCommentDialog(task)}
                        >
                          Comment
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedTask) {
            const taskId = selectedTask._id || selectedTask.id;
            console.log('=== MENU NAVIGATING TO TASK ===');
            console.log('Selected task id:', taskId);
            if (taskId) {
              navigate(`/tasks/${taskId}`);
            }
          }
          handleMenuClose();
        }}>
          <Visibility sx={{ mr: 1 }} /> View Details
        </MenuItem>
        {isAdmin && (
          <>
            <MenuItem onClick={handleMenuClose}>
              <Edit sx={{ mr: 1 }} /> Edit Task
            </MenuItem>
            <MenuItem onClick={handleDeleteTask}>
              <Delete sx={{ mr: 1 }} /> Delete Task
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Comment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={commentDraft}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCommentDraft(e.target.value)}
            placeholder="Add a note for this task…"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitComment} disabled={!!actionLoading}>
            Save comment
          </Button>
          <Button variant="contained" onClick={submitCommentAndDone} disabled={!!actionLoading}>
            Save & mark done
          </Button>
        </DialogActions>
      </Dialog>

      {isAdmin && (
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create New Task</DialogTitle>
          <DialogContent>
            <Typography>
              This will redirect you to the Create Task page where you can fill in all the details.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                setCreateDialogOpen(false);
                window.location.href = '/tasks/create';
              }}
            >
              Go to Create Task
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Tasks;
